/**
 * Merge shell-scoped i18n overrides on top of the static locale bundles.
 *
 * ``GET /public/branding`` always returns the right blob for the
 * caller's host: per-tenant ``Setting`` rows on tenant subdomains,
 * the platform ``PlatformSetting`` row on ``forgmatic.com`` (the
 * default-tenant fallback case — see backend
 * :func:`is_default_tenant_fallback_request`). One merge effect
 * handles both shells; platform-admin shell stays disabled because
 * it never renders public copy.
 *
 * ``i18next.addResourceBundle(deep=true, overwrite=true)`` is
 * idempotent, so re-running on every fetch is cheap.
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useTenantBranding } from "@/api/queries/branding";
import { expandDottedKeys } from "@/i18n/expandDottedKeys";
import { resolveShell } from "@/shell/resolveShell";

export function useShellI18nOverrides(): void {
  const shell = resolveShell();
  const isPublicShell = shell === "tenant" || shell === "platform";
  const branding = useTenantBranding({ enabled: isPublicShell });
  const { i18n } = useTranslation();
  const overrides = branding.data?.i18n_overrides;
  // Stringify so the deps array tracks deep changes; payload is small.
  const fingerprint = overrides ? JSON.stringify(overrides) : "";

  useEffect(() => {
    if (!overrides) return;
    for (const [lang, namespaces] of Object.entries(overrides)) {
      for (const [ns, keys] of Object.entries(namespaces)) {
        // Expand the persisted flat ``"a.b.c"`` paths into a nested
        // object — i18next's default keySeparator splits on ``.`` at
        // lookup time, but addResourceBundle stores keys verbatim, so
        // we have to do the split ourselves before merging.
        i18n.addResourceBundle(lang, ns, expandDottedKeys(keys), true, true);
      }
    }
    // Force re-render of every useTranslation consumer. addResource-
    // Bundle does fire ``store.added`` internally, but we route
    // through ``i18n.emit("languageChanged")`` for symmetry with the
    // edit-mode toggle path (#487 / #488 — the bare ``added`` event
    // on the i18n instance is NOT what useTranslation listens to).
    i18n.emit("languageChanged", i18n.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, i18n]);
}
